import { type ReactNode, useEffect, useState } from "react";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import { prettifyApiError } from "@/shared/api";
import type { Order } from "../types";
import { updateOrderShipping } from "../api";

// Corrieri selezionabili nel dropdown "Corriere" dell'editor spedizione,
// allineati alla whitelist backend ``CarrierCode`` (orders/schemas.py), escluso
// GENERIC che è solo fallback. ``value`` = codice inviato a Poleepo.
export const CARRIER_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "SDA", label: "SDA" },
  { value: "BRT_IT", label: "BRT" },
  { value: "GLS_IT", label: "GLS" },
  { value: "POSTE_ITALIANE", label: "Poste Italiane" },
  { value: "POSTE_DELIVERY", label: "Poste Delivery" },
  { value: "DHL_IT", label: "DHL" },
  { value: "TNT_IT", label: "TNT" },
];

const _CARRIER_ALIAS: Record<string, string> = {
  BRT: "BRT_IT",
  GLS: "GLS_IT",
  DHL: "DHL_IT",
  TNT: "TNT_IT",
  POSTE: "POSTE_ITALIANE",
};

/** Mappa il corriere raw dell'ordine a un ``value`` del dropdown. Fallback SDA
 *  (default backend) se il valore non è riconosciuto. */
function orderCarrierToCode(raw: string | null | undefined): string {
  const up = (raw ?? "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  if (CARRIER_OPTIONS.some((o) => o.value === up)) return up;
  return _CARRIER_ALIAS[up] ?? "SDA";
}

type PkgDim = { kg: number; l: number; a: number; p: number };

// Riga dell'editor colli: PkgDim + una key stabile per il rendering. Con
// key={i} (indice) React riusa i nodi DOM per posizione alla rimozione di una
// riga centrale → focus/caret/IME saltano all'input della vecchia posizione.
// _key deriva dall'identita' della riga, non dall'indice, e NON finisce nel
// payload (save()/onDraftChange mappano solo kg/l/a/p esplicitamente).
type EditorRow = PkgDim & { _key: string };

function _newRow(dim?: PkgDim): EditorRow {
  return {
    kg: dim?.kg ?? 0,
    l: dim?.l ?? 0,
    a: dim?.a ?? 0,
    p: dim?.p ?? 0,
    _key: crypto.randomUUID(),
  };
}

export type ShippingDraft = {
  colli: number;
  dirty: boolean;
  kg: number;
  l: number;
  a: number;
  p: number;
  pallet: boolean;
  /** TUTTE le righe collo dell'editor (redesign sotto-card 2026-07-03):
   *  il salva-implicito di "Genera LDV" persiste la lista completa, non
   *  solo il primo collo. kg/l/a/p singoli restano = dims[0] per i
   *  consumer esistenti. */
  dims: PkgDim[];
  /** Corriere scelto nel dropdown (codice CarrierCode) — usato da "Genera LDV". */
  carrier: string;
};

export function ShippingEditor({
  order,
  onSaved,
  onDraftChange,
  footerActions,
  saveTone = "primary",
}: {
  order: Order;
  onSaved?: () => void;
  onDraftChange?: (draft: ShippingDraft) => void;
  /** Azioni extra nella riga del bottone "Salva" (item 18): la pagina ordine
   *  ci mette "Genera LDV"/"Contestazione" cosi' restano IN LINEA con Salva.
   *  Assente (default OrderCard) → solo Salva, layout invariato. */
  footerActions?: ReactNode;
  /** "secondary" degrada "Salva" a variante contornata quando affianca un
   *  bottone primario (Genera LDV emerald): evita due primari in competizione.
   *  Default "primary" = stile storico (OrderCard). */
  saveTone?: "primary" | "secondary";
}) {
  // Redesign 2026-07-03 (Davide punto 3): righe per TUTTI i colli, non solo
  // il primo. Seed: dimensioni salvate (tutte) → listino fornitore (tutte)
  // → una riga vuota.
  // Lazy initializer: ``_newRow`` chiama ``crypto.randomUUID()`` per ogni riga e
  // il seed serve SOLO al primo mount — nel corpo del componente veniva
  // rieseguito a ogni render (l'editor ri-renderizza a ogni tasto sui campi
  // collo) per un risultato che React scarta subito.
  const [rows, setRows] = useState<EditorRow[]>(() => {
    const seedRows: EditorRow[] = (
      order.packageDimensions && order.packageDimensions.length > 0
        ? order.packageDimensions
        : (order.suggestedPackages ?? []).map((s) => ({
            kg: s.kg ?? 0,
            l: s.l ?? 0,
            a: s.a ?? 0,
            p: s.p ?? 0,
          }))
    ).map((d) => _newRow(d));
    return seedRows.length > 0 ? seedRows : [_newRow()];
  });
  const [colli, setColli] = useState<number>(order.colli > 0 ? order.colli : 1);
  // Checkbox "Bancale" pre-spuntata se Davide l'ha salvata a mano
  // (usePallet) OPPURE se il fornitore l'ha indicato nella mail conferma
  // (usaBancale, dal classifier). Audit 2026-05-22 (ordine 408-9481296-4481150):
  // prima usava solo usePallet → restava vuota anche con usa_bancale=true.
  const [pallet, setPallet] = useState<boolean>(
    order.usePallet === true || order.usaBancale === true,
  );
  // Corriere per la LDV (dropdown): seed dal corriere corrente dell'ordine
  // (fallback SDA). Cambiarlo marca dirty → "Genera LDV" forza la rigenerazione
  // col nuovo corriere.
  const [carrier, setCarrier] = useState<string>(orderCarrierToCode(order.corriere));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // "Sporco": l'utente ha toccato N° Colli/peso/dimensioni/bancale. Quando vero
  // "Genera LDV" forza la rigenerazione (purge+ricrea su ordini non spediti)
  // cosi' i valori della card vincono sull'eventuale LDV in cache (WYSIWYG,
  // bug 2026-06-08). Quando falso, ri-cliccare riusa la LDV in cache.
  const [shippingDirty, setShippingDirty] = useState(false);

  // Riporta la bozza {colli, dirty} al pannello (ExpandPanel) che ha il
  // bottone "Genera LDV": cosi' la generazione usa il N° Colli che l'utente
  // vede qui, senza dover premere prima "Salva" (WYSIWYG, bug 2026-06-08).
  useEffect(() => {
    const first = rows[0] ?? { kg: 0, l: 0, a: 0, p: 0 };
    onDraftChange?.({
      colli: Math.trunc(colli) || 0,
      dirty: shippingDirty,
      kg: _num(first.kg),
      l: _num(first.l),
      a: _num(first.a),
      p: _num(first.p),
      pallet,
      dims: rows.map((r) => ({ kg: _num(r.kg), l: _num(r.l), a: _num(r.a), p: _num(r.p) })),
      carrier,
    });
  }, [colli, shippingDirty, rows, pallet, carrier, onDraftChange]);

  function _num(v: number): number {
    return Number.isFinite(v) && v > 0 ? v : 0;
  }

  function updateRow(index: number, field: keyof PkgDim, value: number) {
    setRows((current) => current.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
    setShippingDirty(true);
  }

  // La nuova lista si calcola FUORI dall'updater: dentro sarebbe un effetto
  // collaterale (React li vuole PURI e in StrictMode li esegue due volte →
  // `setColli` schedulata due volte e un UUID di `_newRow()` bruciato e
  // scartato). In un handler di evento discreto `rows` e' gia' il valore
  // corrente.
  function addRow() {
    const next = [...rows, _newRow()];
    setRows(next);
    setColli(next.length);
    setShippingDirty(true);
  }

  function removeRow(index: number) {
    if (rows.length <= 1) return;
    const next = rows.filter((_, i) => i !== index);
    setRows(next);
    setColli(next.length);
    setShippingDirty(true);
  }

  async function save() {
    // Audit HIGH-2: svuotare "N° Colli" → Number("")=0 → salvare
    // packages_count:0 bloccava "Genera LDV". Validiamo PRIMA di inviare.
    const colliN = Math.trunc(colli);
    if (!Number.isFinite(colliN) || colliN < 1) {
      setErr("Inserisci almeno 1 collo prima di salvare.");
      return;
    }
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      await updateOrderShipping(order.id, {
        packages_count: colliN,
        use_pallet: pallet,
        package_dimensions: rows.map((r) => ({
          kg: _num(r.kg),
          l: _num(r.l),
          a: _num(r.a),
          p: _num(r.p),
        })),
      });
      setMsg("Dati spedizione salvati");
      // I dati sono persistiti: non piu' "sporchi". Senza questo reset, dopo un
      // salvataggio di sole dimensioni (la key di remount non le include) lo
      // stato restava dirty e il successivo "Genera LDV" forzava un purge+POST
      // Poleepo inutile (bug-hunt 2026-06-08).
      setShippingDirty(false);
      onSaved?.();
    } catch (e) {
      setErr(prettifyApiError(e));
    } finally {
      setBusy(false);
    }
  }

  const numCls =
    "w-full px-[10px] py-[7px] bg-card border border-line rounded-sm text-[13px] " +
    "font-mono tabular-nums text-center outline-none transition-colors " +
    "focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent-soft";

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-[0.06em] text-ink-3 font-medium">
              N° Colli
            </span>
            <input
              type="number"
              min="0"
              step="1"
              value={colli}
              title="Numero colli"
              aria-label="Numero colli"
              onChange={(e) => {
                const n = Number(e.target.value);
                setColli(Number.isFinite(n) && n >= 0 ? n : 0);
                setShippingDirty(true);
              }}
              className={`${numCls} w-[64px]`}
            />
          </label>
          <label className="flex items-center gap-1.5 text-[11px] text-ink-2 cursor-pointer">
            <input
              type="checkbox"
              checked={pallet}
              onChange={(e) => {
                setPallet(e.target.checked);
                setShippingDirty(true);
              }}
              className="accent-accent"
            />
            Bancale
          </label>
          <label className="flex items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-[0.06em] text-ink-3 font-medium">
              Corriere
            </span>
            <select
              value={carrier}
              title="Corriere per la LDV"
              aria-label="Corriere"
              onChange={(e) => {
                setCarrier(e.target.value);
                setShippingDirty(true);
              }}
              className="px-[8px] py-[6px] bg-card border border-line rounded-sm text-[12px] outline-none transition-colors focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent-soft"
            >
              {CARRIER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <button
          type="button"
          onClick={addRow}
          className="inline-flex items-center gap-1 px-[10px] py-[5px] text-[11.5px] font-medium rounded-sm border border-line bg-card text-ink-2 hover:bg-paper transition-colors duration-[120ms]"
        >
          + Aggiungi collo
        </button>
      </div>

      {/* Una riga per OGNI collo (Davide 2026-07-03 punto 3): peso e
          dimensioni per-collo visibili e editabili, non solo il primo. */}
      <div className="flex flex-col gap-1.5">
        {rows.map((row, i) => (
          <div
            key={row._key}
            data-testid={`package-row-${i}`}
            className="grid gap-2 items-center"
            style={{ gridTemplateColumns: "56px 1fr 1fr 1fr 1fr 28px" }}
          >
            <span className="font-mono tabular-nums text-[11px] text-ink-3">Collo {i + 1}</span>
            {(
              [
                ["kg", "kg", row.kg, `Peso kg collo ${i + 1}`, "0.1"],
                ["l", "L cm", row.l, `Lunghezza collo ${i + 1}`, "1"],
                ["a", "A cm", row.a, `Altezza collo ${i + 1}`, "1"],
                ["p", "P cm", row.p, `Profondita' collo ${i + 1}`, "1"],
              ] as const
            ).map(([field, short, value, title, step]) => (
              <label key={field} className="flex items-center gap-1 min-w-0">
                <span className="text-[9.5px] uppercase tracking-[0.04em] text-ink-3 font-medium shrink-0 w-[30px] text-right">
                  {short}
                </span>
                <input
                  type="number"
                  min="0"
                  step={step}
                  value={value}
                  title={title}
                  aria-label={title}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    updateRow(i, field, Number.isFinite(n) && n >= 0 ? n : 0);
                  }}
                  className={numCls}
                />
              </label>
            ))}
            <button
              type="button"
              title={`Rimuovi collo ${i + 1}`}
              aria-label={`Rimuovi collo ${i + 1}`}
              disabled={rows.length <= 1}
              onClick={() => removeRow(i)}
              className="w-7 h-7 rounded-sm inline-grid place-items-center text-ink-3 bg-card border border-line hover:text-red hover:border-red disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-[120ms]"
            >
              ×
            </button>
          </div>
        ))}
      </div>

      {/* flex-wrap: in pagina ordine questa riga porta fino a 5 azioni (Salva,
          Contestazione, Genera LDV, Rifai LDV, Invia LDV), tutte
          whitespace-nowrap — senza wrap traboccano su viewport stretti. Nella
          card, dove c'è il solo Salva, non cambia nulla. */}
      <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => void save()}
          disabled={busy}
          className={`px-[14px] py-[7px] rounded-sm text-[13px] font-medium border active:scale-[0.98] transition-[transform,opacity] duration-[120ms] disabled:opacity-50 whitespace-nowrap ${
            saveTone === "secondary"
              ? "border-accent text-accent bg-transparent hover:bg-accent-soft"
              : "bg-accent-2 text-white border-accent hover:brightness-95"
          }`}
        >
          {busy ? "Salvo…" : "Salva"}
        </button>
        {footerActions}
      </div>
      <ErrorBanner message={err} className="mt-2" />
      {msg && !err && (
        <div className="mt-2 rounded-sm border border-green/20 bg-green-soft px-3 py-1.5 text-[12px] text-ink">
          {msg} — pronto per generare la LDV ({rows.length}{" "}
          {rows.length === 1 ? "collo misurato" : "colli misurati"}).
        </div>
      )}
    </div>
  );
}
